import { EsThread, EsThreadPool } from 'threads-es';
import type { MessageWorkerAPI } from '@/assets/workers/message.worker';
import MessageWorker from '@/assets/workers/message.worker?worker';
import { createLogger } from '@/utils/logger';

export default class MessageManager {
  readonly #logger = createLogger('Message');
  #workerPool: EsThreadPool<MessageWorkerAPI> | null = null;

  constructor() {
    MessageManager.#createWorkerPool()
      .then((pool) => {
        this.#workerPool = pool;
      })
      .catch((error) => {
        this.#logger.error(error);
      });
  }

  async dispatch<K extends keyof MessageWorkerAPI>(action: K, ...params: Parameters<MessageWorkerAPI[K]>) {
    return MessageManager.#createWorker().then(async (worker) => {
      const result = await worker.methods[action](...params);

      await worker.terminate();

      return result;
    });
  }

  async queue<K extends keyof MessageWorkerAPI>(action: K, ...params: Parameters<MessageWorkerAPI[K]>) {
    if (!this.#workerPool) {
      this.#workerPool = await MessageManager.#createWorkerPool();
    }

    try {
      return this.#workerPool.queue(async (worker) => await worker.methods[action](...params));
    } catch (error) {
      this.#logger.error(action, params, error);

      throw error;
    }
  }

  static #createWorker() {
    return EsThread.Spawn<MessageWorkerAPI>(new MessageWorker({ name: 'message-worker' }), { timeout: 15_000 });
  }

  static #createWorkerPool() {
    return EsThreadPool.Spawn(
      (id) =>
        EsThread.Spawn<MessageWorkerAPI>(new MessageWorker({ name: `message-worker-${id}` }), { timeout: 15_000 }),
      { size: 6 },
    );
  }
}

export const messageManager = new MessageManager();
